import type { CheckItem } from '@/content/checks'
import type { Photo } from '@/content/sections'
import { LINKS } from '@/content/pages/links'

/**
 * The Future 2030 Pharmaceutical outlook page — its copy and its figures, apart
 * from the component that draws them.
 *
 * **The fifth Future 2030 outlook with a designed page of its own**, and the
 * fourteenth report overall. Asked for on 2026-08-28, in the same words as the
 * Automotive, Manufacturing, Consumer Goods and Retail ones — *"referans alarak
 * ilgili sayfayı birebir tıpa tıp aynısını tasarla"* — as a five-part full-page
 * capture of digitopia.co's Pharmaceutical outlook page, plus five of the images
 * that page carries: the capsule-on-a-circuit-board cover art, the DMI forecast
 * chart and the three requirement photographs. And, as on the Consumer Goods and
 * Retail requests, the instruction that the maturity band point at the 2026 DAIMI
 * report rather than the 2024 one the live page offers.
 *
 * So the bands are the reference's, in the reference's order: hero → the outlook
 * beside Figure 1 → a dark band of three imperatives → the five predictions → the
 * pointer at the maturity report → the download close. The two shared closing
 * bands (`content/reportClosing.ts`) still sit where they sit on every other
 * report page.
 *
 * **The shape is the Retail page's**: two lead-in paragraphs over the predictions,
 * a dark band whose second line is a title rather than a second claim, five plain
 * statements rather than label-and-figure pairs, and the report mockup beside the
 * predictions. That mockup was the one thing missing at build time — the live band's
 * right half is empty and nothing was supplied — and it was sent on 2026-08-28, so
 * the band matches Retail's now. What stays this page's own is its close, which is
 * the live page's own — "Download the Full Report" — rather than the invitation the
 * sibling pages close with.
 *
 * **Three sector slips in the reference, corrected here and flagged.** The live
 * page is visibly built from the Retail one and still says so in three places: the
 * outlook heading reads "A Growing Digital Divide in **Retail**", the predictions
 * lead-in is the Retail paragraph word for word ("From instant gratification to
 * immersive commerce, retail is evolving fast…"), and the maturity CTA reads
 * "Explore the 2024 **Retail** Digital Maturity Report" under a paragraph about the
 * Pharmaceutical report. Transcribing those literally would put another sector's
 * name three times on a pharma page, so each is written for pharma here and each
 * band's `check` says exactly what the reference said and what was done instead.
 *
 * **Two deliberate departures from the capture**, both the settled house call
 * rather than a reading of the reference:
 *
 *  * the h1 is the series headline every outlook page here uses ("Digital
 *    Transformation Outlook for Pharmaceutical") rather than the live page's
 *    "Future 2030: Pharmaceutical Industry Outlook" — the eyebrow above it already
 *    says Future 2030, and the five outlook pages read as one series this way.
 *  * the maturity band carries the 2026 Pharmaceuticals Industry Focus cover, which
 *    the live band does not. `OUTLOOK_PH_MATURITY.check` is where that is asked
 *    about, because it is the one place this page shows something the reference has
 *    not.
 *
 * **Where the words come from.** Everything here is transcribed from the five
 * captures, which are full-page renders read down to fit — the *shape* of the page
 * is certain and the *wording* is a careful reading of body copy set a handful of
 * pixels tall. Every band that carries prose therefore carries a `check`, the same
 * way `content/outlookRetail.ts` does, and `outlookPharmaceuticalChecks()` feeds
 * them into the same manifest so a production build cannot ship them unread.
 *
 * **What the live page has that this page cannot.** Its Download buttons and its
 * closing band open an embedded HubSpot form — first and last name, business email,
 * company, job title, phone, country, industry. These documents are prerendered
 * with no client bundle at all (`src/prerender.tsx`), so a form script would never
 * run, and the other thirty reports on this site hand the reader the PDF from our
 * own bucket instead. Every Download button here is that PDF, and
 * `OUTLOOK_PH_CLOSE.check` is what keeps the gating question open.
 */

/**
 * The report this page belongs to — the branch `pages/Reports.tsx` takes on it.
 *
 * The long slug digitopia.co uses, and the one the request named. It was already
 * in `REPORTS` before this page existed, so the page is built under it as it
 * stands.
 */
export const OUTLOOK_PH_SLUG = 'digital-transformation-outlook-pharmaceutical-industry'

/**
 * The Pharmaceuticals Industry Focus cut of the Digital & AI Maturity Report 2026 —
 * where this page's maturity band sends the reader.
 *
 * Written out rather than imported: that report is one of the twenty drawn by the
 * shared template in `pages/Reports.tsx`, so it has no content module of its own to
 * export a slug from. It is the literal passed to `focus()` in
 * `content/reports.ts`, and the two have to stay in step.
 */
export const DAIMI_PH_SLUG = 'digital-and-ai-maturity-report-2026-pharmaceuticals-industry'

/**
 * The hero.
 *
 * `lede` is set bold above the paragraph because that is how the live hero sets it —
 * one claim in bold, the reason for it underneath — and the button says "Download
 * the Report" rather than the "Download Report" every other band uses, which is also
 * the live page's wording.
 */
export const OUTLOOK_PH_INTRO = {
  lede: 'Pharma’s Digital Future is Already Here',
  body: 'Pharmaceutical companies face a turning point. With AI-enabled drug discovery, digitally enhanced clinical trials, and rising patient expectations, the future is arriving faster than anticipated. Yet a widening digital maturity gap threatens to split the industry between those who lead with innovation and those who fall behind.',
  downloadLabel: 'Download the Report',
  /**
   * The cover art supplied with this request — the capsule on a circuit board,
   * which is the artwork behind the report cover with none of its lettering on it.
   * This is where the live hero puts it too, at the same size and on the same side.
   */
  photo: {
    src: '/photo/future-2030-pharmaceutical-digital-transformation.webp',
    alt: 'A pharmaceutical capsule resting on a glowing circuit board',
  } satisfies Photo,
  check:
    'The hero’s two lines are transcribed from the reference capture rather than supplied as text. Read them back against digitopia.co before production. The image, its side and its size are the live page’s.',
}

/**
 * The outlook band: Figure 1 on the left, the three trajectories on the right.
 *
 * The three are one block on the live page in which each line opens with a bolded
 * label — "Leaders", "Average performers", "Laggards". Split into `label` and `body`
 * here so the label can be set rather than emphasised inside a sentence, which is
 * the call every designed report page on this site has made.
 *
 * No closing line: unlike the Retail and Manufacturing bands, the live band goes
 * straight from the third trajectory to the Download button.
 */
export const OUTLOOK_PH_FIGURE = {
  heading: 'A Growing Digital Divide in Pharma',
  intro: 'Digitopia’s DMI data reveals a critical divide:',
  points: [
    {
      label: 'Leaders',
      body: 'Expected to reach a maturity level of 3.71 by 2029, gaining strategic dominance through AI, smart factories, and personalized medicine.',
    },
    {
      label: 'Average performers',
      body: 'Will improve to 3.13, but risk plateauing without deeper investment.',
    },
    {
      label: 'Laggards',
      body: 'May remain stuck at 2.34, unless they radically reinvent their approach.',
    },
  ],
  /**
   * Figure 1, the file supplied with this request: five years of actuals (2020–24)
   * and five of forecast (2025–29), the leader, average and laggard trajectories
   * each fanning out into an optimistic, moderate and pessimistic band. It is
   * 1200×784 on a white ground, so it gets a white card of its own and scales to it
   * whole — ten year columns with three labelled values each do get small on a
   * phone, but a figure the reader has to drag sideways reads as broken.
   */
  photo: {
    src: '/photo/future-2030-pharmaceutical-dmi-forecast.webp',
    alt: 'Pharmaceutical DMI evolution 2020 to 2029: actual leader, average and laggard scores to 2024, then optimistic, moderate and pessimistic forecast bands to 2029',
  } satisfies Photo,
  caption: [
    'Figure 1: Pharmaceutical Industry DMI Evolution (2020–2029) with Scenario-Based Forecasts',
    'Leader, Average, and Laggard Trajectories under Optimistic, Moderate, and Pessimistic Projections',
  ],
  check:
    'Two things here. The heading is not the reference’s: the live page reads “A Growing Digital Divide in Retail” on a pharmaceutical page, which is a leftover from the Retail outlook it was built from, so it says Pharma here — confirm that is what you want, and it may be worth fixing on digitopia.co too. The three trajectories are transcribed, and their figures agree with Figure 1’s own 2029 labels (3.71 leaders, 3.13 average, 2.34 laggards), so the page and the chart are consistent; the report still has to stand behind them. Separately, the live page calls the index DMI here while every other page on this site calls it DAIMI, the Digital & AI Maturity Index; the chart itself is lettered DMI, so both were left as they are. Say which name this page should use.',
}

/**
 * The dark imperatives band: two headline lines, then three cards.
 *
 * The live band's second line is not a sentence but a title — "3 Imperatives for
 * Pharmaceutical Excellence" — so it is set lighter and larger under the headline
 * rather than as a second claim, which is what the component already does with it.
 */
export const OUTLOOK_PH_REQUIREMENTS = {
  heading: 'Pharma’s Digital Revolution Has Arrived',
  subheading: '3 Imperatives for Pharmaceutical Excellence',
  /** The eyebrow above each card's title, numbered in the order the live band has. */
  eyebrow: 'How to Get There',
  items: [
    {
      title: 'Digitally Integrate Core Operations',
      body: 'Create smart factories, strengthen data governance, and achieve real-time compliance and quality control.',
      linkLabel: 'Digital Solutions',
      linkHref: LINKS.solutions,
      photo: {
        src: '/photo/pharmaceutical-core-operations.webp',
        alt: 'A pharmacist in a white coat working on a tablet in front of stocked shelves',
      } satisfies Photo,
    },
    {
      title: 'Adopt an AI-First Mindset',
      body: 'Use AI in drug discovery, patient engagement, and clinical trials to cut costs and timelines.',
      linkLabel: 'Artificial Intelligence',
      linkHref: LINKS.enterpriseAi,
      photo: {
        src: '/photo/pharmaceutical-ai-first-mindset.webp',
        alt: 'A pharmacist and a customer reading a screen together at a dispensing machine',
      } satisfies Photo,
    },
    {
      title: 'Upskill the Workforce and Evolve the Culture',
      body: 'Build internal capabilities to foster agility, digital confidence, and innovation from within.',
      linkLabel: 'Digital Upskilling',
      linkHref: LINKS.transformation,
      photo: {
        src: '/photo/pharmaceutical-workforce-upskilling.webp',
        alt: 'A pharmacist holding a tablet in a pharmacy aisle',
      } satisfies Photo,
    },
  ],
  check:
    'Two things. The two headline lines and the three card texts are transcribed from the reference capture. And the third card’s link is the one destination that had to change: the live page sends “Digital Upskilling” to a solution page digitopia.co has and this site does not — it came across in the import as a rebuild source rather than as a page, so linking to it would be a dead link. It points at Transformation Management until that page is built. The other two are the live destinations. This is the same substitution the Automotive, Manufacturing, Consumer Goods and Retail outlook pages make, and one answer settles all five.',
}

/**
 * The predictions band.
 *
 * Five plain statements rather than the Automotive page's label-and-figure pairs,
 * because that is what the live band prints. Two lead-in paragraphs rather than one:
 * the live band sets the sector's state of play above the line that introduces the
 * list.
 *
 * **The mockup arrived on 2026-08-28**, which is what this band was missing when the
 * page was built: the live band's right half is empty and nothing was supplied, so
 * the list ran full width rather than being padded out with a file borrowed from
 * another report. The supplied file is this report's own cover, so the band is now
 * the Retail one's — list at two thirds, mockup at one.
 */
export const OUTLOOK_PH_PREDICTIONS = {
  heading: 'Five Predictions Reshaping Pharma by 2030',
  intro: [
    'Pharma is being remade by AI, connected care and data-driven manufacturing. The most adaptive companies are already building AI-first operations, shortening discovery timelines, and designing every trial and treatment around the patient.',
    'The report explores five megatrends shaping the decade:',
  ],
  items: [
    'Precision medicine becomes the standard',
    'AI cuts drug discovery time from years to months',
    'Smart factories power agile, compliant production',
    'Digital patient engagement drives better outcomes',
    'Connected health ecosystems become the norm',
  ],
  /** The mockup supplied on 2026-08-28 — the Future 2030 pharma cover, not a maturity cut's. */
  photo: {
    src: '/photo/future-2030-outlook-pharmaceutical-mockup.webp',
    alt: 'Two copies of Future 2030: Digital Transformation Outlook for Pharma',
  } satisfies Photo,
  check:
    'The heading and all five predictions are transcribed from the reference capture and want a reading rather than a check of a number — none of the five carries a figure. The first lead-in paragraph is NOT the reference’s: the live page prints the Retail outlook’s paragraph here word for word (“From instant gratification to immersive commerce, retail is evolving fast…”), which is another sector on a pharmaceutical page, so it is written for pharma here. Read it and replace it with the intended pharma paragraph if one exists. The second line is the live one. The mockup beside them is the file you supplied on 2026-08-28; the reference capture shows that half of the band empty, so say if the live page really carries no image there and it should come out.',
}

/**
 * The band pointing at the maturity report.
 *
 * **The one band whose content is deliberately not the live page's, and the only
 * change in this request that was asked for in words rather than shown in a
 * picture.** digitopia.co sends the reader to a *2024* report; the instruction with
 * the captures was *"en aşağıda önceden konuştuğumuz gibi 2024'e değil 2026 DAIMI
 * report'a yönlendirelim"*. So the CTA, the cover and the sentence all name the
 * Pharmaceuticals Industry Focus cut of the Digital & AI Maturity Report 2026, which
 * is the report this site actually publishes.
 *
 * That is settled, so it is not what `check` asks about: the same swap was left open
 * on the Automotive page and has now been answered five times.
 */
export const OUTLOOK_PH_MATURITY = {
  heading: 'How Does Your Digital Maturity Compare?',
  body: 'The Pharmaceuticals Industry Focus cut of the Digital & AI Maturity Report 2026 reveals where companies stand today, and where they need to go. Compare your performance with leaders, average performers, and laggards. Are you leading the shift or reacting to it? Use our data to compare your performance, assess your risks, and find the fastest route to transformation.',
  ctaLabel: 'Explore the Pharmaceuticals Industry Focus report',
  ctaHref: `/en/impact-labs/reports/${DAIMI_PH_SLUG}`,
  /** That report's cover, which this site already holds. */
  photo: {
    src: '/photo/daimi-report-2026-pharmaceuticals-cover.webp',
    alt: 'Digital & AI Maturity Report 2026, Pharmaceuticals Focus',
  } satisfies Photo,
  check:
    'The destination here is decided, not transcribed: this band points at the 2026 report rather than the “2024 Pharmaceutical Digital Maturity Report” the live page offers, which is what you asked for — so the CTA label and the sentence naming the report are the 2026 cut’s. Worth knowing that the live CTA button actually reads “Explore the 2024 Retail Digital Maturity Report” under a paragraph about the pharmaceutical one, so the reference was pointing at the wrong sector as well as the wrong year. Two things left to decide. The rest of the paragraph is the live one, so it wants a reading. And the cover beside it is not on the live page at all: it is here because the other four Future 2030 outlook pages show the maturity cut they point at, and it should come out if this band is meant to stay text-only.',
}

/**
 * The closing band, which is where the live page's gated form sits.
 *
 * Its heading and its sentence are the live band's own — the sibling outlook pages
 * close with an invitation ("Lead the Change") because their live close is headed
 * that way; this one is headed "Download the Full Report", so it is.
 */
export const OUTLOOK_PH_CLOSE = {
  heading: 'Download the Full Report',
  body: 'Learn how top experts are preparing for 2030, and how you can join them. Get detailed projections, DMI trend charts, and strategic insights tailored to pharma executives.',
  expertLabel: 'Talk to an expert',
  // `#contact` rather than the Contact Us page: the form sits at the foot of
  // this very page, so the button scrolls instead of navigating away.
  expertHref: '#contact',
  /**
   * Resolved 2026-08-30. This band asked whether the report should sit behind
   * the form the live page gates it with, because every Download button here
   * linked straight to the bucket. It now does: the buttons carry
   * `data-gated-download` (`lib/gatedDownload.ts`), so the reader is asked for
   * an email at the foot of this page and `website-form-lead` sends the PDF to
   * it. There is no direct link left on the page to gate.
   */
  check: undefined,
}

/**
 * The page's open questions, in the shape `build-manifest.json` records copy checks
 * in — the same mechanism `outlookRetailChecks()` uses, and appended to the same
 * list in `reportChecks()`.
 *
 * `'content'` rather than `'asset'`: nothing here is a missing file for a slot this
 * page draws. Every band that carries an image now has one, and the question of
 * whether the predictions band should show a mockup at all — the reference's right
 * half is empty — is asked in its own note rather than recorded as a gap.
 */
export function outlookPharmaceuticalChecks(): CheckItem[] {
  const sections: { section: string; check?: string }[] = [
    { section: 'hero', check: OUTLOOK_PH_INTRO.check },
    { section: 'outlook', check: OUTLOOK_PH_FIGURE.check },
    { section: 'requirements', check: OUTLOOK_PH_REQUIREMENTS.check },
    { section: 'predictions', check: OUTLOOK_PH_PREDICTIONS.check },
    { section: 'maturity', check: OUTLOOK_PH_MATURITY.check },
    { section: 'download', check: OUTLOOK_PH_CLOSE.check },
  ]
  return sections
    .filter((s): s is { section: string; check: string } => Boolean(s.check))
    .map((s) => ({
      locale: 'en',
      page: `impact-labs/reports/${OUTLOOK_PH_SLUG}`,
      section: s.section,
      text: s.check,
      severity: 'content',
    }))
}
